import { ReactElement } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { useEffect } from "react";
import { getApiUrl } from "@/utils/api";
import { useTranslation } from "react-i18next";
import { toast } from "@/utils/toast";
import { VehicleType, FuelType, Country } from "@/types/vehicle";
import { companyAtom } from "@/store/company";
import { useAtom } from "jotai";

interface VehiclesFormProps {
  vehicleId?: number;
  onSuccess?: () => void;
  onCancel?: () => void;
}

// Define Company interface
interface Company {
  id: number;
  name: string;
}

// Define API response interface
interface ApiResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

const vehicleSchema = z.object({
  name: z.string().nullable().optional(),
  license_plate: z.string().min(1, "License plate is required"),
  ownership: z.enum(["rented", "owned"]).default("owned"),
  vehicle_use: z.enum(["feight", "passenger"]).nullable().optional(),
  country: z.number().nullable().optional(),
  vehicle_type: z.number().nullable().optional(),
  fuel_type: z.number().nullable().optional(),
  is_archived: z.boolean().default(false),
  company: z.number().nullable().optional(),
});

type VehicleFormValues = z.infer<typeof vehicleSchema>;

export default function VehiclesForm({
  vehicleId,
  onSuccess,
  onCancel,
}: VehiclesFormProps): ReactElement {
  const { t } = useTranslation();
  const { user, token } = useAuth();
  const queryClient = useQueryClient();
  const [company] = useAtom(companyAtom);

  // Check if user is admin
  const isAdmin = user?.rol === "admin";

  const form = useForm<VehicleFormValues>({
    resolver: zodResolver(vehicleSchema),
    defaultValues: {
      name: "",
      license_plate: "",
      ownership: "owned",
      vehicle_use: "feight",
      country: null,
      vehicle_type: null,
      fuel_type: null,
      is_archived: false,
      company: null,
    },
  });

  // Fetch vehicle data if editing
  const { data, isLoading } = useQuery({
    queryKey: ["vehicle", vehicleId],
    queryFn: async () => {
      if (!vehicleId) return null;
      const response = await fetch(getApiUrl(`vehicles/${vehicleId}/`), {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        throw new Error("Failed to fetch vehicle");
      }
      const responseData = await response.json();
      console.log("API Response for vehicle:", responseData);
      return responseData;
    },
    enabled: !!vehicleId,
  });

  // Fetch vehicle types
  const { data: vehicleTypesData } = useQuery<ApiResponse<VehicleType>>({
    queryKey: ["vehicleTypes"],
    queryFn: async () => {
      const response = await fetch(getApiUrl("vehicle-types/"), {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        throw new Error("Failed to fetch vehicle types");
      }
      return response.json();
    },
  });

  // Fetch fuel types
  const { data: fuelTypesData } = useQuery<ApiResponse<FuelType>>({
    queryKey: ["fuelTypes"],
    queryFn: async () => {
      const response = await fetch(
        getApiUrl("fuel-types/?limit=9999&category=Vehicle"),
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );
      if (!response.ok) {
        throw new Error("Failed to fetch fuel types");
      }
      return response.json();
    },
  });

  // Fetch countries
  const { data: countriesData } = useQuery<ApiResponse<Country>>({
    queryKey: ["countries"],
    queryFn: async () => {
      const response = await fetch(getApiUrl("countries/?limit=9999"), {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        throw new Error("Failed to fetch countries");
      }
      return response.json();
    },
  });

  // Fetch companies for admin users
  const { data: companiesData } = useQuery<ApiResponse<Company>>({
    queryKey: ["companies"],
    queryFn: async () => {
      const response = await fetch(getApiUrl("companies/"), {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        throw new Error("Failed to fetch companies");
      }
      return response.json();
    },
    enabled: isAdmin, // Only fetch if user is admin
  });

  // Track loading state for all select options
  const selectsLoading =
    isLoading ||
    !vehicleTypesData ||
    !fuelTypesData ||
    !countriesData ||
    (isAdmin && !companiesData);

  // Extract data from responses
  const vehicleTypes = vehicleTypesData?.results || [];
  const fuelTypes = fuelTypesData?.results || [];
  const countries = countriesData?.results || [];
  const companies = companiesData?.results || [];

  useEffect(() => {
    if (!selectsLoading) {
      if (data) {
        console.log("Vehicle data loaded:", data);
        // Extract the IDs from nested objects for form fields
        const formValues = {
          name: data.name,
          license_plate: data.license_plate,
          ownership: data.ownership,
          vehicle_use: data.vehicle_use,
          is_archived: data.is_archived,
          // Handle nested objects by extracting their IDs
          country: data.country ? data.country.id : null,
          vehicle_type: data.vehicle_type ? data.vehicle_type.id : null,
          fuel_type: data.fuel_type ? data.fuel_type.id : null,
          company: company?.id || data.company ? data.company.id : null,
        };

        console.log("Setting form values:", formValues);
        form.reset(formValues);
      } else if (!isAdmin && user?.company) {
        // Set default company for non-admin users
        form.setValue("company", user.company.id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, form, isAdmin, user, selectsLoading, company]);

  const mutation = useMutation({
    mutationFn: async (values: VehicleFormValues) => {
      const url = vehicleId
        ? getApiUrl(`vehicles/${vehicleId}/`)
        : getApiUrl("vehicles/");
      const method = vehicleId ? "PATCH" : "POST";

      // If user is not admin, use their company
      if (!isAdmin && user?.company) {
        values.company = user.company.id;
      }

      const response = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(values),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Failed to save vehicle");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vehicles"] });
      toast.success(
        `Vehicle ${vehicleId ? "updated" : "created"} successfully`,
      );
      onSuccess?.();
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const onSubmit = (values: VehicleFormValues) => {
    mutation.mutate(values);
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="flex flex-col h-full pb-24 overflow-y-auto"
      >
        <div className="space-y-6 overflow-y-auto flex-1 px-4">
          {isAdmin && (
            <FormField
              control={form.control}
              name="company"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("vehicles.fields.company")}</FormLabel>
                  <FormControl>
                    <Select
                      onValueChange={(value) => {
                        if (!value || isNaN(Number(value))) {
                          field.onChange(null);
                        } else {
                          field.onChange(Number(value));
                        }
                      }}
                      value={
                        field.value !== undefined && field.value !== null
                          ? field.value.toString()
                          : ""
                      }
                      disabled={selectsLoading}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select company" />
                      </SelectTrigger>
                      <SelectContent>
                        {companies.map((company) => (
                          <SelectItem
                            key={company.id}
                            value={company.id.toString()}
                          >
                            {company.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          <FormField
            control={form.control}
            name="ownership"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("vehicles.fields.ownership")}</FormLabel>
                <FormControl>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select ownership type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="owned">
                        {t("vehicles.ownership.owned")}
                      </SelectItem>
                      <SelectItem value="rented">
                        {t("vehicles.ownership.rented")}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("vehicles.fields.name")}</FormLabel>
                <FormControl>
                  <Input {...field} value={field.value || ""} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="license_plate"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("vehicles.fields.license_plate")}</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="vehicle_use"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("vehicles.fields.vehicle_use")}</FormLabel>
                <FormControl>
                  <Select
                    onValueChange={field.onChange}
                    value={field.value || ""}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select vehicle use" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="feight">
                        {t("vehicles.use.feight")}
                      </SelectItem>
                      <SelectItem value="passenger">
                        {t("vehicles.use.passenger")}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="country"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("vehicles.fields.country")}</FormLabel>
                <FormControl>
                  <Select
                    onValueChange={(value) => {
                      console.log("country value onvaluechange", value);

                      if (!value || isNaN(Number(value))) {
                        field.onChange(null);
                      } else {
                        field.onChange(Number(value));
                      }
                    }}
                    value={
                      field.value !== undefined && field.value !== null
                        ? field.value.toString()
                        : ""
                    }
                    disabled={selectsLoading}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent>
                      {countries.map((country) => (
                        <SelectItem
                          key={country.id}
                          value={country?.id?.toString()}
                        >
                          {country?.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="vehicle_type"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("vehicles.fields.vehicle_type")}</FormLabel>
                <FormControl>
                  <Select
                    onValueChange={(value) => {
                      console.log("vehicle type value onvaluechange", value);

                      if (!value || isNaN(Number(value))) {
                        field.onChange(null);
                      } else {
                        field.onChange(Number(value));
                      }
                    }}
                    value={
                      field.value !== undefined && field.value !== null
                        ? field.value.toString()
                        : ""
                    }
                    disabled={selectsLoading}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select vehicle type" />
                    </SelectTrigger>
                    <SelectContent>
                      {vehicleTypes.map((type) => (
                        <SelectItem key={type?.id} value={type?.id?.toString()}>
                          {type?.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="fuel_type"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("vehicles.fields.fuel_type")}</FormLabel>
                <FormControl>
                  <Select
                    onValueChange={(value) => {
                      if (!value || isNaN(Number(value))) {
                        field.onChange(null);
                      } else {
                        field.onChange(Number(value));
                      }
                    }}
                    value={
                      field.value !== undefined && field.value !== null
                        ? field.value.toString()
                        : ""
                    }
                    disabled={selectsLoading}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select fuel type" />
                    </SelectTrigger>
                    <SelectContent>
                      {fuelTypes.map((type) => (
                        <SelectItem key={type.id} value={type.id.toString()}>
                          {type.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="is_archived"
            render={({ field }) => (
              <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                <FormLabel>{t("vehicles.fields.is_archived")}</FormLabel>
                <FormControl>
                  <Switch
                    onCheckedChange={(checked) => field.onChange(checked)}
                    checked={field.value}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="fixed inset-x-0 bottom-0 bg-background p-4 border-t flex justify-end space-x-2 mt-auto">
          <Button variant="outline" type="button" onClick={onCancel}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="gradient"
            type="submit"
            disabled={mutation.isPending}
          >
            {vehicleId ? t("common.save") : t("common.create")}
          </Button>
        </div>
      </form>
    </Form>
  );
}
